import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Archive, Info } from "lucide-react";
import { cmdApi } from "../api";
import { useCmdAuth } from "../contexts/CmdAuthContext";
import { usePermissions } from "../hooks/usePermissions";
import { fmtDate, fmtDateTime, formatINR, HEALTH_LABEL, STAGE_LABEL } from "../format";

const INV_WELCOME_KEY = "cmd_inv_welcome_dismissed";

function InvestorWelcome({ name }) {
  const [hidden, setHidden] = useState(() => localStorage.getItem(INV_WELCOME_KEY) === "1");
  if (hidden) return null;
  const dismiss = () => { localStorage.setItem(INV_WELCOME_KEY, "1"); setHidden(true); };
  return (
    <div className="cmd-inv-welcome" data-testid="investor-welcome">
      <Info size={18} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>Welcome{name ? `, ${name.split(" ")[0]}` : ""}</div>
        <div style={{ fontSize: 13, lineHeight: 1.55 }}>
          This is the EEPL portfolio view — it's read-only for investors. You'll see every initiative, its health
          and milestones, but you can't edit them. Monthly <strong>published reports</strong> with financial
          figures appear under the <strong>Reports</strong> tab as soon as the team publishes them.
        </div>
      </div>
      <button className="cmd-icon-btn" onClick={dismiss} aria-label="Dismiss welcome note"
        data-testid="investor-welcome-dismiss" style={{ flexShrink: 0 }}>
        <X size={14} />
      </button>
    </div>
  );
}

const STAGE_ORDER = ["running", "building", "pipeline"];
const HEALTH_ORDER = { ok: 0, watch: 1, risk: 2 };

function Sheet({ onClose, title, children, testId }) {
  return (
    <>
      <div className="cmd-sheet-backdrop" onClick={onClose} />
      <div className="cmd-sheet" role="dialog" aria-modal="true" data-testid={testId}>
        <div className="cmd-sheet-head">
          <h2>{title}</h2>
          <button className="cmd-icon-btn" onClick={onClose} aria-label="Close" data-testid={`${testId}-close`}>
            <X size={16} />
          </button>
        </div>
        <div className="cmd-sheet-body">{children}</div>
      </div>
    </>
  );
}

function Kpi({ label, value, danger, testId }) {
  return (
    <div className="cmd-kpi" data-testid={testId}>
      <div className={`cmd-kpi-value ${danger ? "danger" : ""}`}>{value}</div>
      <div className="cmd-kpi-label">{label}</div>
    </div>
  );
}

function Row({ item, onOpen }) {
  const waiting = (item.depends_on || []).filter((d) => d.stage !== "running");
  const healthClass = item.health ? `h-${item.health}` : "";
  return (
    <button
      type="button"
      className={`cmd-init-row ${healthClass}`}
      onClick={() => onOpen(item.id)}
      data-testid={`init-row-${item.id}`}
    >
      <div className="cmd-init-main">
        <div className="cmd-init-name">{item.name}</div>
        <div className="cmd-init-meta">
          {item.next_milestone
            ? <>Next: {item.next_milestone}{item.milestone_date ? ` by ${fmtDate(item.milestone_date)}` : ""}</>
            : <span className="cmd-muted">No next milestone set</span>}
        </div>
        {waiting.length > 0 && (
          <div className="cmd-init-waiting" data-testid={`init-waiting-${item.id}`}>
            Waiting on {waiting.map((w) => w.name).join(", ")}
          </div>
        )}
        {(() => {
          if (!item.reviewed_at) return <div className="cmd-init-stale" data-testid={`init-stale-${item.id}`}>Not reviewed yet</div>;
          const days = (Date.now() - new Date(item.reviewed_at).getTime()) / 86400000;
          return days >= 14
            ? <div className="cmd-init-stale" data-testid={`init-stale-${item.id}`}>Not reviewed in 14+ days</div>
            : null;
        })()}
      </div>
      <div className="cmd-init-side">
        <span className={`cmd-pill stage-${item.stage}`}>{STAGE_LABEL[item.stage]}</span>
        {item.owner_role && <span className="cmd-chip">{item.owner_role}</span>}
        <span className="cmd-init-counts" data-testid={`init-counts-${item.id}`}>
          {item.open_actions} open · {item.late_actions} late
        </span>
      </div>
    </button>
  );
}

function InitiativeForm({ initial, all, roles, onSaved, onClose }) {
  const editing = !!initial?.id;
  const [f, setF] = useState({
    name: initial?.name || "",
    vertical: initial?.vertical || "Coffee",
    stage: initial?.stage || "pipeline",
    owner_role: initial?.owner_role || "Founder",
    health: initial?.health || "",
    partners: initial?.partners || "",
    location: initial?.location || "",
    key_metric: initial?.key_metric || "",
    next_milestone: initial?.next_milestone || "",
    milestone_date: initial?.milestone_date || "",
    depends_on: initial?.depends_on?.map((d) => d.id || d) || [],
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const ownerOptions = roles.filter((r) => r !== "Investor");
  const depOptions = all.filter((i) => i.id !== initial?.id);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleDep = (id) =>
    set("depends_on", f.depends_on.includes(id) ? f.depends_on.filter((x) => x !== id) : [...f.depends_on, id]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...f,
      health: f.health || null,
      milestone_date: f.milestone_date || null,
      next_milestone: f.next_milestone || null,
      partners: f.partners || null,
      location: f.location || null,
      key_metric: f.key_metric || null,
      notes: f.notes || null,
    };
    try {
      const { data } = editing
        ? await cmdApi.patch(`/initiatives/${initial.id}`, payload)
        : await cmdApi.post("/initiatives", payload);
      toast.success(editing ? "Initiative updated" : "Initiative added");
      onSaved(data);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not save initiative.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="init-form">
      <div className="cmd-field">
        <label>Name</label>
        <input className="cmd-input" value={f.name} onChange={(e) => set("name", e.target.value)}
          required maxLength={200} data-testid="init-form-name" />
      </div>
      <div className="cmd-form-grid">
        <div className="cmd-field">
          <label>Vertical</label>
          <select className="cmd-input" value={f.vertical} onChange={(e) => set("vertical", e.target.value)}
            data-testid="init-form-vertical">
            {["Coffee", "Dairy", "Hospitality", "Retail food", "Sri Lanka", "Technology"].map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>
        <div className="cmd-field">
          <label>Stage</label>
          <select className="cmd-input" value={f.stage} onChange={(e) => set("stage", e.target.value)}
            data-testid="init-form-stage">
            {STAGE_ORDER.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
          </select>
        </div>
        <div className="cmd-field">
          <label>Owner role</label>
          <select className="cmd-input" value={f.owner_role} onChange={(e) => set("owner_role", e.target.value)}
            data-testid="init-form-owner">
            {ownerOptions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="cmd-field">
          <label>Health</label>
          <select className="cmd-input" value={f.health} onChange={(e) => set("health", e.target.value)}
            data-testid="init-form-health">
            <option value="">Not rated</option>
            <option value="ok">On track</option>
            <option value="watch">Watch</option>
            <option value="risk">At risk</option>
          </select>
        </div>
      </div>
      <div className="cmd-field">
        <label>Partners & customers</label>
        <input className="cmd-input" value={f.partners} onChange={(e) => set("partners", e.target.value)}
          data-testid="init-form-partners" />
      </div>
      <div className="cmd-form-grid">
        <div className="cmd-field">
          <label>Location</label>
          <input className="cmd-input" value={f.location} onChange={(e) => set("location", e.target.value)}
            data-testid="init-form-location" />
        </div>
        <div className="cmd-field">
          <label>Key number to watch</label>
          <input className="cmd-input" value={f.key_metric} onChange={(e) => set("key_metric", e.target.value)}
            data-testid="init-form-key-metric" />
        </div>
        <div className="cmd-field">
          <label>Next milestone</label>
          <input className="cmd-input" value={f.next_milestone} onChange={(e) => set("next_milestone", e.target.value)}
            data-testid="init-form-milestone" />
        </div>
        <div className="cmd-field">
          <label>By date</label>
          <input type="date" className="cmd-input" value={f.milestone_date}
            onChange={(e) => set("milestone_date", e.target.value)} data-testid="init-form-milestone-date" />
        </div>
      </div>
      <div className="cmd-field">
        <label>Depends on</label>
        <div className="cmd-dep-list" data-testid="init-form-depends">
          {depOptions.length === 0 && <span className="cmd-muted" style={{ fontSize: 13 }}>No other initiatives yet</span>}
          {depOptions.map((d) => (
            <label key={d.id} className="cmd-dep-item">
              <input type="checkbox" checked={f.depends_on.includes(d.id)} onChange={() => toggleDep(d.id)} />
              <span>{d.name}</span>
              <span className="cmd-muted" style={{ fontSize: 12 }}>{STAGE_LABEL[d.stage]}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="cmd-field">
        <label>Notes</label>
        <textarea className="cmd-input" rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)}
          data-testid="init-form-notes" />
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
        <button type="button" className="cmd-btn cmd-btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="cmd-btn cmd-btn-primary" disabled={busy} data-testid="init-form-save">
          {busy ? "Saving…" : editing ? "Save changes" : "Add initiative"}
        </button>
      </div>
    </form>
  );
}

function InitiativeDetail({ id, onClose, onEdit, onArchived, canEdit }) {
  const { can } = usePermissions();
  const [d, setD] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await cmdApi.get(`/initiatives/${id}`);
        setD(data);
      } catch (ex) {
        toast.error(ex?.response?.data?.detail || "Could not load initiative.");
        onClose();
      }
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const archive = async () => {
    try {
      await cmdApi.delete(`/initiatives/${id}`);
      toast.success("Initiative archived");
      onArchived();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not archive.");
    }
  };

  return (
    <Sheet onClose={onClose} title={d ? d.name : "Initiative"} testId="init-detail">
      {!d ? (
        <p className="cmd-muted">Loading…</p>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            <span className={`cmd-pill stage-${d.stage}`}>{STAGE_LABEL[d.stage]}</span>
            {d.health
              ? <span className={`cmd-pill health-${d.health}`}>{HEALTH_LABEL[d.health]}</span>
              : <span className="cmd-chip">Not rated</span>}
            {d.owner_role && <span className="cmd-chip">{d.owner_role}</span>}
          </div>

          <div className="cmd-detail-grid">
            <div><div className="cmd-detail-label">Partners & customers</div><div>{d.partners || <span className="cmd-muted">—</span>}</div></div>
            <div><div className="cmd-detail-label">Location</div><div>{d.location || <span className="cmd-muted">—</span>}</div></div>
            <div><div className="cmd-detail-label">Key number</div><div>{d.key_metric || <span className="cmd-muted">—</span>}</div></div>
            <div><div className="cmd-detail-label">Next milestone</div>
              <div>{d.next_milestone
                ? <>{d.next_milestone}{d.milestone_date ? ` by ${fmtDate(d.milestone_date)}` : ""}</>
                : <span className="cmd-muted">No next milestone set</span>}</div>
            </div>
            <div><div className="cmd-detail-label">Dependencies</div>
              <div>{(d.depends_on || []).length
                ? d.depends_on.map((dep) => (
                    <div key={dep.id}>{dep.name} <span className="cmd-muted" style={{ fontSize: 12 }}>({STAGE_LABEL[dep.stage]})</span></div>
                  ))
                : <span className="cmd-muted">None</span>}</div>
            </div>
            <div><div className="cmd-detail-label">Notes</div><div>{d.notes || <span className="cmd-muted">—</span>}</div></div>
          </div>

          {d.financial_summary !== undefined && (
            <div className="cmd-detail-section" data-testid="init-detail-financials">
              <h4>Recent figures</h4>
              {d.financial_summary.length === 0 ? (
                <p className="cmd-muted" style={{ fontSize: 13 }}>No monthly figures entered yet. Enter them from the Financials tab.</p>
              ) : (
                <table className="cmd-table">
                  <thead><tr><th>Month</th><th>Revenue</th><th>EBITDA</th><th>Cash</th></tr></thead>
                  <tbody>
                    {d.financial_summary.map((frow) => {
                      const ebitda = (frow.revenue || 0) - (frow.cogs || 0) - (frow.staff_cost || 0)
                        - (frow.rent || 0) - (frow.utilities || 0) - (frow.marketing || 0) - (frow.other_opex || 0);
                      return (
                        <tr key={frow.month}>
                          <td>{frow.month}</td>
                          <td>{formatINR(frow.revenue)}</td>
                          <td>{formatINR(ebitda)}</td>
                          <td>{formatINR(frow.cash_balance)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="cmd-detail-section" data-testid="init-detail-actions">
            <h4>Actions ({d.open_actions} open{d.late_actions ? `, ${d.late_actions} late` : ""})</h4>
            {d.actions.length === 0
              ? <p className="cmd-muted" style={{ fontSize: 13 }}>No open actions.</p>
              : d.actions.map((a) => (
                  <div key={a.id} className="cmd-mini-row">
                    <span>{a.title}</span>
                    <span className={`cmd-muted ${a.late ? "cmd-danger-text" : ""}`} style={{ fontSize: 12 }}>
                      {a.due_date ? fmtDate(a.due_date) : "No date"}{a.late ? " · late" : ""}
                    </span>
                  </div>
                ))}
          </div>

          <div className="cmd-detail-section" data-testid="init-detail-decisions">
            <h4>Open decisions ({d.open_decisions})</h4>
            {d.open_decisions_list.length === 0
              ? <p className="cmd-muted" style={{ fontSize: 13 }}>No decisions waiting.</p>
              : d.open_decisions_list.map((dec) => (
                  <div key={dec.id} className="cmd-mini-row">
                    <span>{dec.title}</span>
                    <span className="cmd-muted" style={{ fontSize: 12 }}>{Object.keys(dec.votes || {}).length} votes</span>
                  </div>
                ))}
          </div>

          <div className="cmd-detail-section" data-testid="init-detail-updates">
            <h4>Update feed</h4>
            {d.updates.length === 0
              ? <p className="cmd-muted" style={{ fontSize: 13 }}>No updates posted yet.</p>
              : d.updates.map((u) => (
                  <div key={u.id} className="cmd-update">
                    <div>{u.text}</div>
                    <div className="cmd-muted" style={{ fontSize: 12 }}>{fmtDateTime(u.created_at)}</div>
                  </div>
                ))}
          </div>

          <div className="cmd-detail-actions">
            {canEdit && (
              <button className="cmd-btn" onClick={() => onEdit(d)} data-testid="init-detail-edit">Edit initiative</button>
            )}
            {can("board", "edit") && (
              <button className="cmd-btn cmd-btn-ghost" onClick={() => toast.info("Action board arrives in Prompt 4")} data-testid="init-detail-add-action">Add action</button>
            )}
            {can("decisions", "edit") && (
              <button className="cmd-btn cmd-btn-ghost" onClick={() => toast.info("Decisions arrive in Prompt 5")} data-testid="init-detail-raise-decision">Raise decision</button>
            )}
            {can("financials", "edit") && (
              <button className="cmd-btn cmd-btn-ghost" onClick={() => toast.info("Financials arrive in Prompt 6")} data-testid="init-detail-enter-figures">Enter figures</button>
            )}
            {canEdit && (
              <button className="cmd-btn cmd-btn-ghost" onClick={archive} style={{ marginLeft: "auto", color: "var(--cmd-health-risk)" }}
                data-testid="init-detail-archive">
                <Archive size={14} style={{ marginRight: 6 }} />Archive
              </button>
            )}
          </div>
        </>
      )}
    </Sheet>
  );
}

export default function CmdPortfolio() {
  const { can, role } = usePermissions();
  const { me } = useCmdAuth();
  const canEdit = can("portfolio", "edit");
  const [items, setItems] = useState(null);
  const [roles, setRoles] = useState([]);
  const [viewAs, setViewAs] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try {
      const [{ data: list }, { data: cfg }] = await Promise.all([
        cmdApi.get("/initiatives"),
        cmdApi.get("/roles-config"),
      ]);
      setItems(list.items);
      setRoles(cfg.roles || []);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not load portfolio.");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (items || []).filter((i) => !viewAs || i.owner_role === viewAs),
    [items, viewAs]
  );

  const kpis = useMemo(() => ({
    risk: filtered.filter((i) => i.health === "risk").length,
    watch: filtered.filter((i) => i.health === "watch").length,
    late: filtered.reduce((s, i) => s + (i.late_actions || 0), 0),
    decisions: filtered.reduce((s, i) => s + (i.open_decisions || 0), 0),
  }), [filtered]);

  const byVertical = useMemo(() => {
    const order = ["Coffee", "Dairy", "Hospitality", "Retail food", "Sri Lanka", "Technology"];
    const map = {};
    for (const i of filtered) (map[i.vertical] ||= []).push(i);
    return order.filter((v) => map[v]?.length).map((v) => ({ vertical: v, items: map[v] }));
  }, [filtered]);

  const onSaved = (saved) => {
    setFormOpen(false); setEditing(null);
    load();
  };

  return (
    <section data-testid="cmd-portfolio">
      {role === "Investor" && <InvestorWelcome name={me?.user?.name} />}
      {/* Portfolio strip */}
      <div className="cmd-strip" data-testid="portfolio-strip">
        {STAGE_ORDER.map((stage) => {
          const inStage = filtered.filter((i) => i.stage === stage);
          if (!inStage.length) return null;
          return (
            <div className="cmd-strip-group" key={stage}>
              <div className="cmd-strip-label">{STAGE_LABEL[stage]} {inStage.length}</div>
              <div className="cmd-strip-track">
                {inStage.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    className={`cmd-strip-seg seg-${i.health || "none"}`}
                    title={i.name}
                    aria-label={i.name}
                    onClick={() => setOpenId(i.id)}
                    data-testid={`strip-seg-${i.id}`}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* KPI tiles */}
      <div className="cmd-kpis">
        <Kpi label="At risk" value={kpis.risk} danger={kpis.risk > 0} testId="kpi-at-risk" />
        <Kpi label="On watch" value={kpis.watch} testId="kpi-on-watch" />
        <Kpi label="Overdue actions" value={kpis.late} danger={kpis.late > 0} testId="kpi-overdue" />
        <Kpi label="Decisions waiting" value={kpis.decisions} testId="kpi-decisions" />
      </div>

      {/* Toolbar */}
      <div className="cmd-toolbar">
        <div className="cmd-field" style={{ margin: 0, minWidth: 220 }}>
          <label style={{ marginBottom: 4 }}>Viewing as</label>
          <select className="cmd-input" value={viewAs} onChange={(e) => setViewAs(e.target.value)}
            data-testid="viewing-as">
            <option value="">Whole company</option>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        {canEdit && (
          <button className="cmd-btn cmd-btn-primary" style={{ marginLeft: "auto" }}
            onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-initiative">
            <Plus size={16} style={{ marginRight: 6 }} />Add initiative
          </button>
        )}
      </div>

      {/* Grouped rows */}
      {items === null ? (
        <p className="cmd-muted">Loading…</p>
      ) : byVertical.length === 0 ? (
        <div className="cmd-empty" data-testid="portfolio-empty">
          <h3>No initiatives to show</h3>
          <p>{viewAs ? `Nothing owned by ${viewAs}. Try “Whole company”.` : "Add your first initiative to start tracking the portfolio."}</p>
        </div>
      ) : (
        byVertical.map(({ vertical, items: list }) => (
          <div key={vertical} style={{ marginBottom: 24 }}>
            <h3 className="cmd-vertical-head" data-testid={`vertical-${vertical.replace(/\s/g, "-")}`}>{vertical}</h3>
            {list.map((i) => <Row key={i.id} item={i} onOpen={setOpenId} />)}
          </div>
        ))
      )}

      {/* Sheets */}
      {formOpen && (
        <Sheet
          onClose={() => { setFormOpen(false); setEditing(null); }}
          title={editing ? "Edit initiative" : "Add initiative"}
          testId="init-form-sheet"
        >
          <InitiativeForm
            initial={editing}
            all={items || []}
            roles={roles.length ? roles : ["Founder", "Co-founder", "CFO", "Finance Director", "Vertical lead", "Investor", "Staff", "Unassigned"]}
            onSaved={onSaved}
            onClose={() => { setFormOpen(false); setEditing(null); }}
          />
        </Sheet>
      )}
      {openId && !formOpen && (
        <InitiativeDetail
          id={openId}
          onClose={() => setOpenId(null)}
          canEdit={canEdit}
          onEdit={(d) => { setEditing(d); setFormOpen(true); setOpenId(null); }}
          onArchived={() => { setOpenId(null); load(); }}
        />
      )}
    </section>
  );
}
